import React, {useState} from 'react';
import {ConfirmDialog} from 'src/design/molecules/ConfirmDialog';
import {PageHeader} from 'src/design/organisms/PageHeader';
import {
  useCloseOutCourseClassroomMutation,
  useIssueCourseClassroomCertificatesMutation,
  useUndoCourseClassroomCloseoutMutation,
} from 'src/redux/queries/courseClassroom';
import {useAlert} from 'src/shared/hooks/useAlert';
import {parseErrorMessage} from 'src/shared/utils/parseErrorMessage';
import type {IStateMachineTransition} from 'src/types/StateMachine';
import {CloseoutReviewDialog} from './CloseoutReviewDialog';

// Wave 4 transition names — mirrors backend CourseClassroomTransitionNames
// (Proact.Shared/Services/Courses/CourseClassroomStateMachine.cs). Not
// exposed on the generated client (state machine transitions are data, not
// typed contracts), so the literals are duplicated here deliberately.
const TRANSITION_COMPLETE_AND_CLOSEOUT = 'complete-and-closeout';
const TRANSITION_COMPLETE_NO_CLOSEOUT = 'complete-no-closeout';
const TRANSITION_CANCEL = 'cancel';

export interface ClassCloseoutActionsProps {
  classroomId: string;
  findTransition: (name: string) => IStateMachineTransition | undefined;
  executeTransition: (transition: IStateMachineTransition) => Promise<void>;
  isTransitioning: boolean;
  canUndoCloseout: boolean;
  /** Class reached Completed without a close-out — either "Complete Class" was
   * used, or a close-out died part-way. Every state-machine transition is
   * From=InProgress, so this action is the only way back. */
  canCloseOutLate: boolean;
  showIssueCertificates: boolean;
  instructorName?: string;
  /** Course name — seeds the Oversight tab's search box when the closeout
   * preview links out to pending demonstrations for this class. */
  courseName?: string;
}

/**
 * The Class Workspace's complete/close-out/cancel/undo/issue-certificates
 * flow: the PageHeader actions that trigger it plus every dialog it opens.
 * Rendered as a child of `PageHeader.Actions` — safe because `Actions` (see
 * `PageHeader.tsx`) renders its children directly with no type whitelist;
 * only `PageHeader`'s own top-level children (Title/Badges/Actions/...) are
 * filtered by displayName.
 */
export const ClassCloseoutActions: React.FC<ClassCloseoutActionsProps> = ({
  classroomId,
  findTransition,
  executeTransition,
  isTransitioning,
  canUndoCloseout,
  canCloseOutLate,
  showIssueCertificates,
  instructorName,
  courseName,
}) => {
  const {setAlert} = useAlert();

  const [closeoutDialogOpened, setCloseoutDialogOpened] = useState(false);
  const [lateCloseoutDialogOpened, setLateCloseoutDialogOpened] =
    useState(false);
  const [confirmCompleteOpened, setConfirmCompleteOpened] = useState(false);
  const [confirmCancelOpened, setConfirmCancelOpened] = useState(false);
  const [confirmUndoOpened, setConfirmUndoOpened] = useState(false);
  const [confirmIssueOpened, setConfirmIssueOpened] = useState(false);

  const [undoCloseout, {isLoading: isUndoing}] =
    useUndoCourseClassroomCloseoutMutation();
  const [closeOutLate, {isLoading: isClosingOut}] =
    useCloseOutCourseClassroomMutation();
  const [issueCertificates, {isLoading: isIssuing}] =
    useIssueCourseClassroomCertificatesMutation();

  const handleCloseout = async () => {
    const transition = findTransition(TRANSITION_COMPLETE_AND_CLOSEOUT);
    if (!transition) {
      return;
    }
    await executeTransition(transition);
    setCloseoutDialogOpened(false);
  };

  const handleLateCloseout = async () => {
    const receipt = await closeOutLate(parseInt(classroomId))
      .unwrap()
      .catch(error => {
        setAlert(parseErrorMessage(error), 'error');
        return undefined;
      });
    if (!receipt) {
      return;
    }
    setLateCloseoutDialogOpened(false);
    const alreadySignedOff =
      receipt.alreadySignedOffCount > 0
        ? `, ${receipt.alreadySignedOffCount} already signed off`
        : '';
    setAlert(
      `Closed out: completed ${receipt.completedCount}, failed ${receipt.failedCount}, left open ${receipt.staysOpenCount}${alreadySignedOff}.`,
      'success'
    );
  };

  const handleCompleteWithoutCloseout = async () => {
    const transition = findTransition(TRANSITION_COMPLETE_NO_CLOSEOUT);
    setConfirmCompleteOpened(false);
    if (!transition) {
      return;
    }
    await executeTransition(transition);
  };

  const handleCancel = async () => {
    const transition = findTransition(TRANSITION_CANCEL);
    setConfirmCancelOpened(false);
    if (!transition) {
      return;
    }
    await executeTransition(transition);
  };

  const handleUndoCloseout = async () => {
    setConfirmUndoOpened(false);
    const receipt = await undoCloseout(parseInt(classroomId))
      .unwrap()
      .catch(error => {
        setAlert(parseErrorMessage(error), 'error');
        return undefined;
      });
    if (!receipt) {
      return;
    }
    setAlert(
      `Undid close-out: reverted ${receipt.revertedAssignmentCount} assignment(s) and removed ${receipt.deletedSessionCount} sign-off session(s).`,
      'success'
    );
  };

  const handleIssueCertificates = async () => {
    setConfirmIssueOpened(false);
    const response = await issueCertificates(parseInt(classroomId))
      .unwrap()
      .catch(error => {
        setAlert(parseErrorMessage(error), 'error');
        return undefined;
      });
    if (!response) {
      return;
    }
    // Every attendee is accounted for by exactly one count, so a zero-issue run
    // states why rather than leaving the admin to guess.
    const reasons = [
      response.skippedNotCompleteCount > 0 &&
        `${response.skippedNotCompleteCount} not complete`,
      response.skippedCompletedElsewhereCount > 0 &&
        `${response.skippedCompletedElsewhereCount} completed outside this class`,
      response.skippedAlreadyIssuedCount > 0 &&
        `${response.skippedAlreadyIssuedCount} already issued`,
    ].filter(Boolean);

    setAlert(
      `Issued ${response.issuedCount} certificate(s)${
        reasons.length > 0 ? `; skipped ${reasons.join(', ')}` : ''
      }.`,
      'success'
    );
  };

  return (
    <>
      {findTransition(TRANSITION_COMPLETE_AND_CLOSEOUT) && (
        <PageHeader.Action
          variant="primary"
          onClick={() => setCloseoutDialogOpened(true)}>
          Review &amp; Close Out
        </PageHeader.Action>
      )}
      {findTransition(TRANSITION_COMPLETE_NO_CLOSEOUT) && (
        <PageHeader.Action onClick={() => setConfirmCompleteOpened(true)}>
          Complete Class
        </PageHeader.Action>
      )}
      {findTransition(TRANSITION_CANCEL) && (
        <PageHeader.Action
          variant="danger"
          onClick={() => setConfirmCancelOpened(true)}>
          Cancel Class
        </PageHeader.Action>
      )}
      {canCloseOutLate && (
        <PageHeader.Action
          variant="primary"
          onClick={() => setLateCloseoutDialogOpened(true)}>
          Review &amp; Close Out
        </PageHeader.Action>
      )}
      {canUndoCloseout && (
        <PageHeader.Action
          variant="danger"
          onClick={() => setConfirmUndoOpened(true)}>
          Undo Close-out
        </PageHeader.Action>
      )}
      {showIssueCertificates && (
        <PageHeader.Action onClick={() => setConfirmIssueOpened(true)}>
          Issue Certificates
        </PageHeader.Action>
      )}

      <CloseoutReviewDialog
        opened={closeoutDialogOpened}
        onClose={() => setCloseoutDialogOpened(false)}
        classroomId={parseInt(classroomId)}
        isSaving={isTransitioning}
        onConfirm={handleCloseout}
        courseName={courseName}
      />

      <CloseoutReviewDialog
        opened={lateCloseoutDialogOpened}
        onClose={() => setLateCloseoutDialogOpened(false)}
        classroomId={parseInt(classroomId)}
        isSaving={isClosingOut}
        onConfirm={handleLateCloseout}
        courseName={courseName}
      />

      <ConfirmDialog
        opened={confirmCompleteOpened}
        onClose={() => setConfirmCompleteOpened(false)}
        onConfirm={handleCompleteWithoutCloseout}
        confirmationButtonColor="green"
        title="Complete class?"
        body="This marks the class as completed. It does not change any attendee's training status; use Review & Close Out for that."
      />

      <ConfirmDialog
        opened={confirmCancelOpened}
        onClose={() => setConfirmCancelOpened(false)}
        onConfirm={handleCancel}
        confirmationButtonColor="red"
        title="Cancel class?"
        body="This marks the class as cancelled. It does not change any attendee's training status."
      />

      <ConfirmDialog
        opened={confirmUndoOpened}
        onClose={() => setConfirmUndoOpened(false)}
        onConfirm={handleUndoCloseout}
        confirmationButtonColor="red"
        title="Undo close-out?"
        body="This reverts the sign-offs this close-out created and reopens the affected assignments. Available for 24 hours after close-out."
        confirmationButtonDisabled={isUndoing}
      />

      <ConfirmDialog
        opened={confirmIssueOpened}
        onClose={() => setConfirmIssueOpened(false)}
        onConfirm={handleIssueCertificates}
        confirmationButtonColor="green"
        title="Issue certificates?"
        body={`This issues a certificate for every eligible attendee in this class, crediting ${
          instructorName ?? 'the class instructor'
        }.`}
        confirmationButtonDisabled={isIssuing}
      />
    </>
  );
};
